import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "./supabaseClient";
import type { AppUser } from "./types";

type AuthStatus =
  | "loading" // ainda verificando sessao existente
  | "signed_out" // sem sessao
  | "checking_allowlist" // logado, checando app_users
  | "allowed" // logado + na allowlist ativa
  | "not_allowed"; // logado, mas sem linha ativa em app_users

interface AuthContextValue {
  status: AuthStatus;
  session: Session | null;
  user: User | null;
  appUser: AppUser | null;
  signInWithGoogle: () => Promise<void>;
  signInWithPassword: (
    email: string,
    password: string,
  ) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [session, setSession] = useState<Session | null>(null);
  const [appUser, setAppUser] = useState<AppUser | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadAllowlist(currentSession: Session | null) {
      if (!currentSession) {
        if (!cancelled) {
          setSession(null);
          setAppUser(null);
          setStatus("signed_out");
        }
        return;
      }

      if (!cancelled) {
        setSession(currentSession);
        setStatus("checking_allowlist");
      }

      // Usuario autenticado sem linha ATIVA em app_users nao enxerga nada:
      // a RLS ja garante isso no banco, mas checar aqui evita renderizar
      // telas que so vao falhar silenciosamente com listas vazias.
      const { data, error } = await supabase
        .from("app_users")
        .select("*")
        .eq("id", currentSession.user.id)
        .eq("ativo", true)
        .maybeSingle();

      if (cancelled) return;

      if (error || !data) {
        setAppUser(null);
        setStatus("not_allowed");
        return;
      }

      setAppUser(data as AppUser);
      setStatus("allowed");
    }

    supabase.auth.getSession().then(({ data }) => {
      void loadAllowlist(data.session);
    });

    const { data: subscription } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        void loadAllowlist(newSession);
      },
    );

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      session,
      user: session?.user ?? null,
      appUser,
      async signInWithGoogle() {
        await supabase.auth.signInWithOAuth({
          provider: "google",
          options: {
            redirectTo: window.location.origin,
          },
        });
      },
      async signInWithPassword(email, password) {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        return { error: error?.message ?? null };
      },
      async signOut() {
        await supabase.auth.signOut();
      },
    }),
    [status, session, appUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth precisa ser usado dentro de <AuthProvider>.");
  }
  return ctx;
}
